// EKS Plugin Migration - v2.0
import React, { lazy, Suspense } from 'react';
import { Navigate, Route } from 'react-router-dom';
import { ApiExplorerPage } from '@backstage/plugin-api-docs';
import './global.css';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import {
  ReportIssue,
  LightBox,
} from '@backstage/plugin-techdocs-module-addons-contrib';
import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';
import { CustomCatalogPage } from './components/catalog/CustomCatalogPage';
import { LibrariesPage } from './components/libraries/LibrariesPage';
import { HomePage } from './components/home/HomePage';
import { PullRequestsPage } from './components/PullRequestsPage';
import { AssignedTicketsPage } from './components/AssignedTicketsPage';
import { DocsPage } from './components/docs/DocsPage';
import { DocsRedirect } from './components/docs/DocsRedirect';
import { DevinChatButton } from './components/DevinChatButton';

// Lazy load heavy pages for better initial load performance
const SquadsPage = lazy(() =>
  import('./components/squads/SquadsPage').then(m => ({
    default: m.SquadsPage,
  })),
);
const JiraProjectsPage = lazy(() =>
  import('./components/jira/JiraProjectsPage').then(m => ({
    default: m.JiraProjectsPage,
  })),
);
const ShortcutsPage = lazy(() =>
  import('./components/shortcuts/ShortcutsPage').then(m => ({
    default: m.ShortcutsPage,
  })),
);
const MSKClustersPage = lazy(() =>
  import('./components/msk/MSKClustersPage').then(m => ({
    default: m.MSKClustersPage,
  })),
);
const MskClustersNonProdPage = lazy(() =>
  import('./components/msk/MskClustersNonProdPage').then(m => ({
    default: m.MskClustersNonProdPage,
  })),
);
const MskClustersProdPage = lazy(() =>
  import('./components/msk/MskClustersProdPage').then(m => ({
    default: m.MskClustersProdPage,
  })),
);
const EventCatalogPage = lazy(() =>
  import('./components/msk/EventCatalogPage').then(m => ({
    default: m.EventCatalogPage,
  })),
);
const DoraMetricsPage = lazy(() =>
  import('./components/dora/DoraMetricsPage').then(m => ({
    default: m.DoraMetricsPage,
  })),
);
const DevinUsagePage = lazy(() =>
  import('./components/devinUsage/DevinUsagePage').then(m => ({
    default: m.DevinUsagePage,
  })),
);
const PlatformSupportPage = lazy(() =>
  import('./components/support/PlatformSupportPage').then(m => ({
    default: m.PlatformSupportPage,
  })),
);
const WfpSquadsPage = lazy(() =>
  import('./components/wfp/WfpSquadsPage').then(m => ({
    default: m.WfpSquadsPage,
  })),
);
const SecurityPage = lazy(() =>
  import('./components/security').then(m => ({ default: m.SecurityPage })),
);
const NewsletterPage = lazy(() =>
  import('./components/newsletter/NewsletterPage').then(m => ({
    default: m.NewsletterPage,
  })),
);
const NewsletterDetailPage = lazy(() =>
  import('./components/newsletter/NewsletterDetailPage').then(m => ({
    default: m.NewsletterDetailPage,
  })),
);
import { FinOpsPage } from '@internal/plugin-finops';
import { EKSPage } from '@internal/plugin-eks';
import { ManagementPage } from '@internal/plugin-management';

import {
  AlertDisplay,
  OAuthRequestDialog,
  Progress,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { microsoftAuthApiRef } from '@backstage/core-plugin-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { spinAppThemes } from './theme/spinTheme';
import { RoleBasedRoute } from './components/RoleBasedRoute';
import { SinglePageAwareReaderLayout } from './components/techdocs/SinglePageAwareReaderLayout';
import { TechDocsHeadingSize } from './components/techdocs/TechDocsHeadingSize';
import { TechDocsMermaidViewer } from './components/techdocs/TechDocsMermaidViewer';

const app = createApp({
  apis,
  themes: spinAppThemes,
  components: {
    SignInPage: props => (
      <SignInPage
        {...props}
        auto
        onSignInSuccess={async identityApi => {
          try {
            const { userEntityRef } = await identityApi.getBackstageIdentity();

            window.DD_RUM?.onReady(() => {
              window.DD_RUM?.setUser({
                id: userEntityRef,
              });

              window.DD_RUM?.addAction('backstage_login_success', {
                provider: 'microsoft',
              });
            });
          } catch (error) {
            console.warn('Unable to configure Datadog RUM user', error);
          } finally {
            // Required for Backstage to complete the sign-in.
            props.onSignInSuccess(identityApi);
          }
        }}
        provider={{
          id: 'microsoft-auth-provider',
          title: 'Microsoft',
          message: 'Sign in using Microsoft Entra ID',
          apiRef: microsoftAuthApiRef,
        }}
      />
    ),
  },
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createComponent: catalogPlugin.routes.catalogIndex,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomePage />} />
    <Route path="/pull-requests" element={<PullRequestsPage />} />
    <Route path="/assigned-tickets" element={<AssignedTicketsPage />} />
    <Route
      path="/squads"
      element={
        <Suspense fallback={<Progress />}>
          <SquadsPage />
        </Suspense>
      }
    />
    <Route
      path="/shortcuts"
      element={
        <Suspense fallback={<Progress />}>
          <ShortcutsPage />
        </Suspense>
      }
    />
    <Route
      path="/newsletter"
      element={
        <Suspense fallback={<Progress />}>
          <NewsletterPage />
        </Suspense>
      }
    />
    <Route
      path="/newsletter/:slug"
      element={
        <Suspense fallback={<Progress />}>
          <NewsletterDetailPage />
        </Suspense>
      }
    />
    <Route path="/eks/*" element={<EKSPage />} />
    <Route
      path="/msk-clusters"
      element={
        <Suspense fallback={<Progress />}>
          <MSKClustersPage />
        </Suspense>
      }
    />
    <Route
      path="/msk-clusters-nonprod"
      element={
        <Suspense fallback={<Progress />}>
          <MskClustersNonProdPage />
        </Suspense>
      }
    />
    <Route
      path="/msk-clusters-prod"
      element={
        <Suspense fallback={<Progress />}>
          <MskClustersProdPage />
        </Suspense>
      }
    />
    <Route
      path="/kafka/event-catalog"
      element={
        <Suspense fallback={<Progress />}>
          <EventCatalogPage />
        </Suspense>
      }
    />
    <Route
      path="/jira-projects"
      element={
        <Suspense fallback={<Progress />}>
          <JiraProjectsPage />
        </Suspense>
      }
    />
    <Route
      path="/dora-metrics"
      element={
        <Suspense fallback={<Progress />}>
          <DoraMetricsPage />
        </Suspense>
      }
    />
    <Route
      path="/devin-usage"
      element={
        <Suspense fallback={<Progress />}>
          <DevinUsagePage />
        </Suspense>
      }
    />
    <Route path="/finops/*" element={<FinOpsPage />} />
    <Route path="/catalog" element={<CustomCatalogPage />} />
    <Route path="/catalog-index" element={<CatalogIndexPage />} />
    <Route path="/libraries" element={<LibrariesPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />}>
      <Navigate to="/docs/engineering" replace />
    </Route>
    <Route
      path="/docs/engineering"
      element={<DocsRedirect document="engineering" />}
    />
    <Route path="/docs/cli" element={<DocsRedirect document="cli" />} />
    <Route path="/docs/libs" element={<DocsPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <SinglePageAwareReaderLayout />
      <TechDocsAddons>
        <TechDocsHeadingSize />
        <ReportIssue />
        <LightBox />
        <Mermaid />
        <TechDocsMermaidViewer />
      </TechDocsAddons>
    </Route>
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route
      path="/platform-support"
      element={
        <Suspense fallback={<Progress />}>
          <PlatformSupportPage />
        </Suspense>
      }
    />
    <Route
      path="/wfp-squads"
      element={
        <Suspense fallback={<Progress />}>
          <WfpSquadsPage />
        </Suspense>
      }
    />
    <Route
      path="/security/*"
      element={
        <RoleBasedRoute requireSecTeam>
          <Suspense fallback={<Progress />}>
            <SecurityPage />
          </Suspense>
        </RoleBasedRoute>
      }
    />
    <Route
      path="/management"
      element={
        <RoleBasedRoute allowedRoles={['admin']}>
          <ManagementPage />
        </RoleBasedRoute>
      }
    />
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
    <DevinChatButton />
  </>,
);
